import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Fuse from "fuse.js";

interface SearchableSelectorProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  emptyMessage?: string;
  allowClear?: boolean;
  showCount?: boolean;
}

export default function SearchableSelector({
  title,
  value,
  onChange,
  options,
  placeholder = "Search and select...",
  loading = false,
  disabled = false,
  className = "",
  emptyMessage = "No options available",
  allowClear = true,
  showCount = true,
}: SearchableSelectorProps) {
  const { t } = useTranslation("common");

  // Custom SVG Icons
  const ChevronDownIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );

  const CheckIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );

  const XMarkIcon = ({ className }: { className?: string }) => (
    <svg className={className || "h-3 w-3"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const SearchIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(options, {
      threshold: 0.3, // 0.0 = exact match, 1.0 = match anything
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 1
    });
  }, [options]);

  // Filter options using fuzzy search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;

    const results = fuse.search(searchQuery);
    return results.map(result => result.item);
  }, [fuse, searchQuery, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1,
          );
          break;
        case "Enter":
          event.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length
          ) {
            handleSelect(filteredOptions[highlightedIndex]);
          }
          break;
        case "Escape":
          event.preventDefault();
          setIsOpen(false);
          setSearchQuery("");
          setHighlightedIndex(-1);
          break;
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, filteredOptions, highlightedIndex]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    onChange("");
    setSearchQuery("");
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const getDisplayValue = () => {
    if (!value) return placeholder;
    return value;
  };

  const getSelectedOption = () => {
    return options.find((option) => option === value);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-xs text-text-dark/70 mb-1 font-medium">
        {title}
        {showCount && options.length > 0 && (
          <span className="ml-1 text-text-dark/50">({options.length})</span>
        )}
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled || loading}
          className={`
            w-full text-left px-3 py-2.5 text-sm border border-gray-200 rounded-lg
            bg-background text-text-dark shadow-sm
            focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
            transition-all duration-200
            ${
              disabled || loading
                ? "opacity-50 cursor-not-allowed bg-gray-50"
                : "hover:border-primary/30 hover:shadow-md cursor-pointer"
            }
            ${isOpen ? "ring-2 ring-primary/20 border-primary shadow-md" : ""}
          `}
        >
          <div className="flex items-center justify-between">
            <span
              className={`truncate ${!value ? "text-text-dark/50" : "text-text-dark font-medium"}`}
            >
              {getDisplayValue()}
            </span>
            <div className="flex items-center space-x-1 ml-2">
              {allowClear && value && !disabled && !loading && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  className="p-1 text-text-dark/40 hover:text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer"
                  title="Clear selection"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      handleClear();
                    }
                  }}
                >
                  <XMarkIcon />
                </div>
              )}
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              ) : (
                <div className={`text-text-dark/40 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
                  <ChevronDownIcon />
                </div>
              )}
            </div>
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-56 overflow-hidden ring-1 ring-black/5">
            {/* Search Input */}
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-dark/40">
                  <SearchIcon />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search options..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setHighlightedIndex(-1);
                  }}
                  className="block w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 text-text-dark placeholder-text-dark/50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white transition-all duration-200"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-dark/40 hover:text-text-dark/70 transition-colors"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>

            {/* Options List */}
            <div className="p-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              {filteredOptions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-8 h-8 text-text-dark/30 mb-3">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                    </svg>
                  </div>
                  <p className="text-sm text-text-dark/60 text-center font-medium">
                    {searchQuery
                      ? `No matches for "${searchQuery}"`
                      : emptyMessage}
                  </p>
                  {searchQuery && (
                    <p className="text-xs text-text-dark/40 text-center mt-1">
                      Try different keywords
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredOptions.map((option, index) => {
                    const isSelected = option === value;
                    const isHighlighted = index === highlightedIndex;

                    return (
                      <button
                        key={option}
                        onClick={() => handleSelect(option)}
                        className={`
                          group w-full px-3 py-2 text-left text-sm transition-all duration-200
                          flex items-center justify-between border border-transparent rounded-md
                          hover:bg-primary-50 hover:border-primary/10 cursor-pointer
                          ${
                            isHighlighted
                              ? "bg-primary-50 text-primary border-primary/20"
                              : "text-text-dark"
                          }
                          ${isSelected ? "bg-primary-100 border-primary/30 text-primary font-medium" : ""}
                        `}
                      >
                        <span className="truncate group-hover:text-primary transition-colors duration-200">
                          {option}
                        </span>
                        {isSelected && (
                          <div className="text-primary flex-shrink-0 ml-2">
                            <CheckIcon />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer with count */}
            {filteredOptions.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
                <div className="flex items-center justify-center text-xs text-text-dark/60">
                  <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 17.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  <span className="font-medium">
                    {filteredOptions.length} of {options.length} options
                    {searchQuery && ` matching "${searchQuery}"`}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
